import type {ValuationData} from "@reva/shared/valuation-data";

import type ServiceApi from "../../api";
import {ErrorI18n} from "../../i18n";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";
import {WebError} from "../web-error";

/** Fehlerschlüssel, wenn es zur angefragten ID keine REVA-ID gibt (404, R200). */
export const PERSISTENCE_NOT_FOUND_ERROR_KEY = "address.error.no_persistence_found";

/**
 * API interface for loading a single valuation from the persistence (GET {@link RevaApi.PATH_VALUATIONS}/{id}).
 */
export class PersistenceFetchApiInterface extends SimpleApiInterface<ValuationData> {
    /**
     * Unique identifier of the valuation to load. This is typically a RevaId, but the endpoint also
     * resolves other kinds of unique valuation identifiers (e.g. an external order number or a
     * value order GUID).
     */
    protected readonly id: string;
    private readonly forceUpdateExternalData: boolean | null;

    constructor(id: string, forceUpdateExternalData: boolean | null = null) {
        super();
        this.id = id;
        this.forceUpdateExternalData = forceUpdateExternalData;
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([RevaApi.PATH_VALUATIONS, this.id], {
            forceUpdateExternalData: this.forceUpdateExternalData,
        });
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<ValuationData> {
        return executor.fetchGET(url, credentials);
    }

    protected handleApiErrors(e: Error): ValuationData {
        if (e instanceof WebError && e.status === 404) {
            if (e.getErrorCode() === "R200") {
                throw new ErrorI18n(PERSISTENCE_NOT_FOUND_ERROR_KEY);
            }
            if (e.getErrorCode() === "R220") {
                throw new ErrorI18n("address.error.no_snapshot_found");
            }
        }
        return super.handleApiErrors(e);
    }
}
